import { authFetch } from '@/auth/token-interceptor';
import { env } from '@/env';
import { ApiError } from './errors';

const API_BASE = env.apiUrl + '/api';

/**
 * Typed fetch helper for backend API calls. Use only for endpoints missing
 * from the OpenAPI spec - prefer `openapiClient` (api/openapi-client.ts).
 *
 * Failures throw an `ApiError` carrying the HTTP status, same as `unwrapResult()`. A bare `Error` used to be thrown instead, which erased the
 * status: callers could no longer tell an expired session (401, already handled
 * by the token interceptor) from an unreachable api, so they reported both as a
 * connectivity failure.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  // Parse defensively and only AFTER the status is known. An error response is
  // not always JSON — a gateway 502 sends an HTML page — and parsing it first
  // threw a SyntaxError that masked the real status.
  const text = await res.text();
  let json: unknown = null;
  if (text.length > 0) {
    try {
      json = JSON.parse(text);
    } catch {
      json = res.ok ? null : { message: text };
    }
  }

  if (!res.ok) {
    throw ApiError.from(json, res.status);
  }

  return json as T;
}
